import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { checkVoucher } from './TrackBalancePage.duck';

import css from './TrackBalancePage.module.css';
import voucherDisplay from './img/voucherDisplay.svg';

const TrackBalancePanel = (props) => {

  const { voucherData, userVouchers, fetchingUserVouchers, currentUser } = props;
  
  const [code, setCode] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'used'
  const dispatch = useDispatch();
  const intl = useIntl();



  const { checking, error } = useSelector(state => state.TrackBalancePage);

  const handleCheck = () => {
    if (code.trim()) {
      dispatch(checkVoucher(code));
    }
  };

  // Filter vouchers based on balance
  const activeVouchers = userVouchers?.filter(voucher => parseFloat(voucher.balance) > 0) || [];
  const usedVouchers = userVouchers?.filter(voucher => parseFloat(voucher.balance) === 0) || [];
  
  const currentVouchers = activeTab === 'active' ? activeVouchers : usedVouchers;

  // Debug: Log voucher data to see what fields are available
  
  // Alert to make debugging more visible (temporary)
  if (userVouchers && userVouchers.length > 0) {
  }

  return (
    <div className={css.panel}>
      <h2 className={css.heading}>
        <FormattedMessage id="TrackBalancePanel.title" defaultMessage="Track Your Vouchers" />
      </h2>

      {/* User's Vouchers Section - only show if logged in */}
      {currentUser && (
        <div className={css.userVouchersSection}>
          <h3 className={css.subHeading}>
            <FormattedMessage 
              id="TrackBalancePanel.yourVouchers" 
              defaultMessage="Your Vouchers ({email})"
              values={{ email: currentUser.attributes?.email }}
            />
          </h3>
          
          {fetchingUserVouchers ? (
            <p className={css.loading}>
              <FormattedMessage id="TrackBalancePanel.loading" defaultMessage="Loading your vouchers..." />
            </p>
          ) : userVouchers && userVouchers.length > 0 ? (
            <>
              {/* Tab Navigation */}
              <div className={css.tabNavigation}>
                <button
                  className={`${css.tabButton} ${activeTab === 'active' ? css.active : ''}`}
                  onClick={() => setActiveTab('active')}
                >
                  <FormattedMessage 
                    id="TrackBalancePanel.activeVouchers" 
                    defaultMessage="Active Vouchers" 
                  />
                  <span className={css.tabCount}>{activeVouchers.length}</span>
                </button>
                <button
                  className={`${css.tabButton} ${activeTab === 'used' ? css.active : ''}`}
                  onClick={() => setActiveTab('used')}
                >
                  <FormattedMessage 
                    id="TrackBalancePanel.usedVouchers" 
                    defaultMessage="Used Vouchers" 
                  />
                  <span className={css.tabCount}>{usedVouchers.length}</span>
                </button>
              </div>

              {/* Voucher Grid */}
              {currentVouchers.length > 0 ? (
                <div className={css.vouchersGrid}>
                  {currentVouchers.map((voucher, index) => (
                    <div key={voucher.id || index} className={css.voucherCardContainer}>
                      <div className={css.voucherCard}>
                        <img src={voucherDisplay} className={css.voucherBackground} alt="Gift Card" />
                        
                        {/* Overlay content - matching checkout page layout */}
                        <div className={css.voucherTitle}>
                          {voucher.listingTitle || voucher.productName || '1 Session'}
                        </div>
                        <div className={css.voucherRecipient}>
                          Recipient: {voucher.recipientName}
                        </div>
                        <div className={css.voucherEmail}>
                          Email: {currentUser?.attributes?.email}
                        </div>
                      </div>
                      <div className={css.voucherBalanceInfo}>
                        <span className={css.balanceLabel}>Remaining Balance:</span>
                        <span className={`${css.balanceAmount} ${parseFloat(voucher.balance) === 0 ? css.zero : ''}`}>
                          ${voucher.balance}
                        </span>
                        
                        {/* Voucher Details */}
                        <div className={css.voucherDetails}>
                          {/* Debug: Log individual voucher data */}
                          {console.log('Debug - Individual voucher:', voucher)}
                          {console.log('Debug - voucher.code:', voucher.code)}
                          {console.log('Debug - voucher.expiryDate:', voucher.expiryDate)}
                          {console.log('Debug - voucher.debug_metadata:', voucher.debug_metadata)}
                          <div className={css.voucherDetail}>
                            <span className={css.detailLabel}>Card Number:</span>
                            <span className={css.detailValue}>#{voucher.code || voucher.id || 'N/A'}</span>
                          </div>
                          <div className={css.voucherDetail}>
                            <span className={css.detailLabel}>Expires:</span>
                            <span className={css.detailValue}>
                              {voucher.expiryDate 
                                ? new Date(voucher.expiryDate).toLocaleDateString()
                                : 'No expiry date set'
                              }
                            </span>
                          </div>
                          {/* Show debugging info when expiryDate is missing */}
                          {!voucher.expiryDate && (
                            <div className={css.voucherDetail}>
                              <span className={css.detailLabel}>Debug:</span>
                              <span className={css.detailValue}>No expiry date</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={css.emptyTabMessage}>
                  <div className={css.emptyTabIcon}>
                    {activeTab === 'active' ? '💳' : '✅'}
                  </div>
                  <div className={css.emptyTabText}>
                    {activeTab === 'active' ? (
                      <FormattedMessage 
                        id="TrackBalancePanel.noActiveVouchers" 
                        defaultMessage="No active vouchers" 
                      />
                    ) : (
                      <FormattedMessage 
                        id="TrackBalancePanel.noUsedVouchers" 
                        defaultMessage="No used vouchers" 
                      />
                    )}
                  </div>
                  <div className={css.emptyTabSubtext}>
                    {activeTab === 'active' ? (
                      <FormattedMessage 
                        id="TrackBalancePanel.noActiveVouchersText" 
                        defaultMessage="You don't have any vouchers with remaining balance." 
                      />
                    ) : (
                      <FormattedMessage 
                        id="TrackBalancePanel.noUsedVouchersText" 
                        defaultMessage="You haven't fully used any vouchers yet." 
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className={css.noVouchers}>
              <FormattedMessage 
                id="TrackBalancePanel.noVouchers" 
                defaultMessage="No vouchers found for your email address." 
              />
            </p>
          )}
        </div>
      )}

      {/* Manual Voucher Check Section */}
      <div className={css.manualCheckSection}>
        <h3 className={css.subHeading}>
          <FormattedMessage id="TrackBalancePanel.manualCheck" defaultMessage="Check Another Voucher" />
        </h3>
      <p className={css.subtext}>
        <FormattedMessage
          id="TrackBalancePanel.description"
          defaultMessage="Enter the voucher number below and we’ll let you know how much you have and when it expires."
        />
      </p>

      <div className={css.inputRow}>
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder={intl.formatMessage({ id: 'TrackBalancePanel.inputPlaceholder' })}
          className={css.input}
        />
        <button onClick={handleCheck} className={css.button} disabled={checking}>
          <FormattedMessage id="TrackBalancePanel.checkButton" defaultMessage="Check" />
        </button>
      </div>

      {error && (
        <p className={css.error}>
          <FormattedMessage id={error} />
        </p>
      )}

      {voucherData && (
        <>
          <p className={css.resultText}>
            <FormattedMessage id="TrackBalancePanel.resultText" />
          </p>
          <div className={css.voucherCardContainer} style={{ maxWidth: '400px', margin: '1rem 0' }}>
            <div className={css.voucherCard}>
              <img src={voucherDisplay} className={css.voucherBackground} alt="Gift Card" />
              
              {/* Overlay content for manual check result */}
              <div className={css.voucherTitle}>
                {voucherData?.listingTitle || voucherData?.productName || '1 Session'}
              </div>
              <div className={css.voucherRecipient}>
                Voucher Code: #{code}
              </div>
              <div className={css.voucherEmail}>
                Valid voucher code
              </div>
            </div>
            <div className={css.voucherBalanceInfo}>
              <span className={css.balanceLabel}>Remaining Balance:</span>
              <span className={`${css.balanceAmount} ${parseFloat(voucherData?.balance) === 0 ? css.zero : ''}`}>
                ${voucherData?.balance}
              </span>
              
              {/* Voucher Details for manual check */}
              <div className={css.voucherDetails}>
                <div className={css.voucherDetail}>
                  <span className={css.detailLabel}>Card Number:</span>
                  <span className={css.detailValue}>#{code}</span>
                </div>
                {voucherData?.expiryDate && (
                  <div className={css.voucherDetail}>
                    <span className={css.detailLabel}>Expires:</span>
                    <span className={css.detailValue}>
                      {new Date(voucherData.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default TrackBalancePanel;

